import * as ToastPrimitive from "@radix-ui/react-toast";
import { X } from "lucide-react";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ToastItem = {
  action?: {
    label: string;
    onClick: () => void;
  };
  description?: string;
  id: number;
  title: string;
};

const ToastContext = createContext<{
  pushToast: (toast: Omit<ToastItem, "id">) => void;
} | null>(null);

// `duration` is overridable so tests can pass Infinity to disable the
// auto-dismiss timer. Radix's Root reads Infinity as "never schedule a
// timeout", which prevents a stray setTimeout from firing after JSDOM
// teardown and throwing `ReferenceError: document is not defined`.
export function ToastProvider({
  children,
  duration = 3000,
}: {
  children: ReactNode;
  duration?: number;
}) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const value = useMemo(
    () => ({
      pushToast: (toast: Omit<ToastItem, "id">) => {
        setItems((current) => [
          ...current,
          { ...toast, id: Date.now() + current.length },
        ]);
      },
    }),
    [],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            className="grid w-[360px] gap-1 rounded-2xl border border-primary/20 bg-primary-soft/85 p-4 shadow-lg backdrop-blur-sm"
            duration={duration}
            onOpenChange={(open) => {
              if (!open) {
                setItems((current) =>
                  current.filter((entry) => entry.id !== item.id),
                );
              }
            }}
            open
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-1">
                <ToastPrimitive.Title className="text-sm font-medium text-primary-dark">
                  {item.title}
                </ToastPrimitive.Title>
                {item.description ? (
                  <ToastPrimitive.Description className="text-sm text-primary-dark/75">
                    {item.description}
                  </ToastPrimitive.Description>
                ) : null}
              </div>
              {item.action ? (
                <ToastPrimitive.Action asChild altText={item.action.label}>
                  <button
                    className="rounded-pill border border-primary/25 bg-card px-3 py-1.5 text-sm font-medium text-primary-dark hover:bg-primary/10"
                    onClick={item.action.onClick}
                    type="button"
                  >
                    {item.action.label}
                  </button>
                </ToastPrimitive.Action>
              ) : null}
              <ToastPrimitive.Close
                className="rounded-full p-1 text-primary-dark/70 hover:bg-primary/10 hover:text-primary-dark"
                data-slot="interactive-control"
              >
                <X className="h-4 w-4" />
              </ToastPrimitive.Close>
            </div>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[60] flex max-w-full flex-col gap-3 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }

  return context;
}
